import { Box, Flex, Icon as ChakraIcon, Image, Text } from '@chakra-ui/react';
import type { ResponsiveValue } from '@chakra-ui/react';
import { Calendar, MapPin } from 'lucide-react';
import type { ReactNode } from 'react';

/** About V2 sections share the Thmanyah type ramp (see {@link HeroSection}). */
const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', sans-serif";

export type EventCardVariant = 'standard' | 'featured';

export interface EventCardProps {
  /** Cover image URL. */
  image: string;
  /** Accessible alt text for the cover image. Falls back to empty (decorative). */
  imageAlt?: string;
  /**
   * Short category label. On `standard` it renders as a frosted pill in the
   * top-start corner; on `featured` it renders as an eyebrow above the title.
   */
  category: ReactNode;
  /** Event title. */
  title: ReactNode;
  /** Optional date line (shown with a calendar icon). */
  date?: ReactNode;
  /** Optional venue line (shown with a pin icon). */
  venue?: ReactNode;
  /** Optional popularity badge (e.g. "+8K") shown as a dark pill in the top-end corner. */
  badge?: ReactNode;
  /**
   * Optional coral highlight pill shown in the top-start corner of the `featured`
   * variant (e.g. "الحدث الأكبر"). Ignored on the standard variant.
   */
  highlight?: ReactNode;
  /** 'featured' renders the larger variant. */
  variant?: EventCardVariant;
  /** When provided, the whole card becomes a focusable link. */
  href?: string;
}

/** Frosted-glass pill, used for the standard category tag. */
const FrostedPill = ({ children }: { children: ReactNode }) => (
  <Text
    as='span'
    m={0}
    px='12px'
    py='5px'
    borderRadius='9999px'
    bg='rgba(255, 255, 255, 0.18)'
    backdropFilter='blur(12px)'
    border='1px solid rgba(255, 255, 255, 0.28)'
    color='white'
    fontFamily={FONT_FAMILY}
    fontWeight='500'
    fontSize='12px'
    lineHeight='140%'
    whiteSpace='nowrap'
  >
    {children}
  </Text>
);

/** Solid coral pill, used for the featured highlight tag. */
const CoralPill = ({ children }: { children: ReactNode }) => (
  <Text
    as='span'
    m={0}
    px='12px'
    py='5px'
    borderRadius='9999px'
    bg='#FF5547' // accent-coral
    color='white'
    fontFamily={FONT_FAMILY}
    fontWeight='600'
    fontSize='12px'
    lineHeight='140%'
    whiteSpace='nowrap'
  >
    {children}
  </Text>
);

/** Dark translucent pill, used for the popularity/attendance badge. */
const BadgePill = ({ children }: { children: ReactNode }) => (
  <Text
    as='span'
    // 'auto' keeps a leading "+" on the correct side (e.g. "+8K" not "8K+") regardless
    // of the surrounding RTL context, while Arabic-led badges stay right-to-left.
    dir='auto'
    m={0}
    px='12px'
    py='5px'
    borderRadius='9999px'
    bg='rgba(0, 0, 0, 0.45)'
    backdropFilter='blur(12px)'
    border='1px solid rgba(255, 255, 255, 0.18)'
    color='white'
    fontFamily={FONT_FAMILY}
    fontWeight='500'
    fontSize='12px'
    lineHeight='140%'
    whiteSpace='nowrap'
  >
    {children}
  </Text>
);

/** A single meta item (date or venue) — icon leads on the right in RTL, text trails. */
const MetaItem = ({ icon, children }: { icon: typeof Calendar; children: ReactNode }) => (
  <Flex align='center' gap='6px'>
    <ChakraIcon
      as={icon}
      boxSize='14px'
      color='whiteAlpha.800'
      strokeWidth={2}
      flexShrink={0}
      aria-hidden
    />
    <Text
      as='span'
      m={0}
      color='whiteAlpha.800'
      fontFamily={FONT_FAMILY}
      fontWeight='400'
      fontSize={{ base: '12px', md: '13px' }}
      lineHeight='140%'
      whiteSpace='nowrap'
    >
      {children}
    </Text>
  </Flex>
);

/**
 * Event card (TZ-381) for the "من فعالياتنا" carousel: a full-bleed cover image with
 * the title and meta overlaid over a bottom gradient scrim. RTL throughout.
 *
 * - `standard` (default): frosted category pill in the top-start corner, optional
 *   popularity badge in the top-end corner, and the title + date/venue at the bottom.
 * - `featured`: the larger variant — an optional coral highlight pill in the top-start
 *   corner, optional badge in the top-end corner, and a category eyebrow above the
 *   (bigger) title at the bottom.
 *
 * The cover uses `objectFit: cover`, so it keeps its aspect ratio without distorting
 * on resize. When `href` is set the root is an anchor, so the card is keyboard-focusable.
 */
export const EventCard = ({
  image,
  imageAlt = '',
  category,
  title,
  date,
  venue,
  badge,
  highlight,
  variant = 'standard',
  href,
}: EventCardProps) => {
  const isFeatured = variant === 'featured';
  const interactive = href != null;

  const interactiveProps = interactive
    ? {
        as: 'a' as const,
        href,
        _hover: { textDecoration: 'none' },
        _focusVisible: { outline: '2px solid', outlineColor: '#FF5547', outlineOffset: '3px' },
      }
    : {};

  // Top-start corner: coral highlight on featured, frosted category on standard.
  const topStart = isFeatured
    ? highlight != null && <CoralPill>{highlight}</CoralPill>
    : category != null && <FrostedPill>{category}</FrostedPill>;

  // Per the Figma spec: 24px radius, content laid out top-to-bottom with
  // space-between (pills pinned to the top, title/meta to the bottom) inside a
  // padding that grows on large screens (featured 40px, standard 24px; 20px on mobile).
  const contentPad: ResponsiveValue<string> = isFeatured
    ? { base: '20px', md: '40px' }
    : { base: '20px', md: '24px' };

  return (
    <Flex
      {...interactiveProps}
      dir='rtl'
      direction='column'
      justify='space-between'
      position='relative'
      overflow='hidden'
      borderRadius='24px'
      p={contentPad}
      w='100%'
      h='100%'
      bg='#1B1917'
    >
      {/* Cover + scrim sit behind the content (absolute, so padding doesn't inset them). */}
      <Image
        src={image}
        alt={imageAlt}
        position='absolute'
        inset={0}
        w='100%'
        h='100%'
        objectFit='cover'
      />

      {/* Bottom-up scrim with a light tinted floor at the top, so the overlaid text
          and the corner pills stay legible over any cover (light or dark). */}
      <Box
        position='absolute'
        inset={0}
        sx={{
          background:
            'linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.55) 32%, rgba(0, 0, 0, 0.28) 62%, rgba(0, 0, 0, 0.24) 100%)',
        }}
      />

      {/* Top row (always present so space-between keeps the content pinned to the
          bottom even when there are no pills). */}
      <Flex position='relative' zIndex={1} align='flex-start' justify='space-between' gap='8px'>
        <Box>{topStart}</Box>
        {badge != null && <BadgePill>{badge}</BadgePill>}
      </Flex>

      <Flex
        position='relative'
        zIndex={1}
        direction='column'
        gap={isFeatured ? '10px' : '8px'}
        textAlign='start'
      >
        {isFeatured && category != null && (
          <Text
            as='span'
            m={0}
            color='whiteAlpha.800'
            fontFamily={FONT_FAMILY}
            fontWeight='500'
            fontSize={{ base: '13px', md: '14px' }}
            lineHeight='140%'
          >
            {category}
          </Text>
        )}

        <Text
          as='h3'
          m={0}
          color='white'
          fontFamily={FONT_FAMILY}
          fontWeight='800'
          fontSize={isFeatured ? { base: '22px', md: '26px' } : { base: '16px', md: '18px' }}
          lineHeight='120%'
          noOfLines={2}
        >
          {title}
        </Text>

        {(date != null || venue != null) && (
          <Flex align='center' rowGap='6px' columnGap='16px' flexWrap='wrap'>
            {date != null && <MetaItem icon={Calendar}>{date}</MetaItem>}
            {venue != null && <MetaItem icon={MapPin}>{venue}</MetaItem>}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
